import * as React from 'react'
import styled, { css } from 'styled-components'
import Close from 'assets/plus.svg'
import theme from 'theme'

export type Props = {
  className?: string
  onClose: () => void
  children?: any
  /** set to true when something is in
   * the upper right corner so it wont
   * overlap with close icon */
  desktopExtendedMargin?: boolean
  blurred?: boolean
  $maxWidth?: string
  'data-cy-state'?: string
}

export default function ModalTemplate(props: Props) {
  const [blurred, setBlurred] = React.useState(false)
  React.useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = ''
    }
  }, [])
  React.useEffect(() => {
    setBlurred(props.blurred || false)
  }, [props.blurred])

  const scrollRef = React.useRef<HTMLDivElement | null>(null)

  const onScroll = () => {
    if (scrollRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollRef.current
      if (scrollTop + clientHeight === scrollHeight) {
        setBlurred(false)
      } else if (props.blurred) {
        setBlurred(true)
      }
    }
  }

  return (
    <Wrapper
      className="ModalTemplate"
      data-cy-state={props['data-cy-state']}
      $extendMargin={props.desktopExtendedMargin}
      $maxWidth={props.$maxWidth ? props.$maxWidth : '40rem'}
      data-cy-ctx="molecules/account/ModalTemplate"
    >
      <div
        className="overlay"
        onClick={props.onClose}
        data-cy-handle="overlay-ref-click"
      />
      <div className="outer-box">
        <div className="box" onScroll={() => onScroll()} ref={scrollRef}>
          <div
            className="close"
            onClick={props.onClose}
            data-cy-handle="close-btn"
          >
            <Close />
          </div>
          <div className={props.className}>{props.children}</div>
        </div>
        {blurred && <div data-cy-state="has-blur" className="blur" />}
      </div>
    </Wrapper>
  )
}

const Wrapper = styled.div<{ $extendMargin?: boolean; $maxWidth: string }>`
  > .overlay {
    z-index: ${theme.zIndex.theme_molecules_account_modaltemplate_Overlay};
    position: fixed;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.7);
  }
  > .outer-box {
    z-index: ${theme.zIndex.theme_molecules_account_modaltemplate_OuterBox};
    position: fixed;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    background: ${theme.colors.shade.b7};

    > .box {
      max-height: calc(100vh - 2.5rem);
      width: calc(100vw - 40px);
      max-width: ${(p) => p.$maxWidth};
      padding: ${theme.spacing('m')};
      overflow: auto;
      @media (min-width: ${theme.ms.MD}px) {
        padding: ${theme.spacing('l')};
        ${(p) =>
          p.$extendMargin &&
          css`
            padding-top: ${theme.spacing('xl')};
          `}
      }

      > .close {
        transform: rotate(45deg);
        z-index: ${theme.zIndex.theme_molecules_account_modaltemplate_Close};
        position: absolute;
        cursor: pointer;
        top: ${theme.spacing('xs')};
        right: ${theme.spacing('xs')};
        @media (min-width: ${theme.ms.MD}px) {
          top: ${theme.spacing('m')};
          right: ${theme.spacing('m')};
        }
        > svg {
          width: 20px;
        }
      }
    }
    > .blur {
      background-image: linear-gradient(
        to bottom,
        rgba(255, 0, 0, 0),
        rgba(255, 255, 255)
      );
      z-index: ${theme.zIndex.theme_molecules_account_modaltemplate_Blur};
      pointer-events: none;
      height: 100px;
      width: 100%;
      position: fixed;
      bottom: 0px;
    }
  }
`
